import { Suspense, useId, useMemo, useState } from 'react';
import { MessageIcon, ReportIcon } from '../icons';
import { Badge } from '../Badge';
import { Button } from '../Button';
import { MessageVisuals, VisualBoundary } from '../visuals/MessageVisuals';
import { retryableLazy } from '../visuals/retryableLazy.js';
import { reportEmailSpec } from '../email/emailDraft.js';
import { expiryState, normalizeDocumentSpec } from '../documents/documentSpec.js';
import './ReportMessage.css';

// Carte « Brouillon d'e-mail » : chunk chargé au premier clic sur « Envoyer par e-mail »
const EmailDraftCard = retryableLazy(() => import('../email/EmailDraftCard.jsx'));

/**
 * Message « système » d'un rapport composé (DOC-17) : produit sans appel au modèle,
 * il porte la carte du document (skeleton → carte, ou note d'erreur). Une fois le
 * document prêt, « Envoyer par e-mail » ouvre un brouillon avec le rapport en pièce jointe ;
 * rapport expiré (fichiers conservés 24 h) : bouton désactivé, raison affichée.
 */
export function ReportMessage({ message }) {
  const [emailOpen, setEmailOpen] = useState(false);
  const doc = message.artifacts?.find((a) => a.status === 'ready' && a.spec?.kind === 'document' && typeof a.spec.download_url === 'string');
  const emailSpec = useMemo(() => (doc ? reportEmailSpec(doc.spec) : null), [doc]);
  const expired = doc ? expiryState(normalizeDocumentSpec(doc.spec)).expired : false;
  const hintId = useId();
  return (
    <div className="report-message" role="group" aria-label="Rapport composé">
      <p className="report-message__head">
        <ReportIcon size={14} />
        <span className="report-message__title">Rapport composé</span>
        <Badge tone="success" className="report-message__badge" title="Assemblé par le serveur à partir des visuels déjà vérifiés, sans appel au modèle">
          Sans IA · aucun coût
        </Badge>
      </p>
      {message.content && <p className="report-message__text">{message.content}</p>}
      <MessageVisuals artifacts={message.artifacts} messageId={message.id} label="Document du rapport" />
      {emailSpec && !emailOpen && (
        <div className="report-message__actions">
          <Button variant="outline" size="sm" icon={MessageIcon} onClick={() => setEmailOpen(true)} disabled={expired} aria-describedby={expired ? hintId : undefined}>
            <span>Envoyer par e-mail</span>
          </Button>
          {expired && <span id={hintId} className="report-message__hint">Rapport expiré : composez-le de nouveau pour l'envoyer par e-mail.</span>}
        </div>
      )}
      {emailSpec && emailOpen && (
        <VisualBoundary>
          <Suspense fallback={<p className="report-message__text">Ouverture du brouillon…</p>}>
            <EmailDraftCard rawSpec={emailSpec} artifactId={`${doc.id}:email`} />
          </Suspense>
        </VisualBoundary>
      )}
    </div>
  );
}
